/**
 * Gate tiers from quote.gate_breakdown (writer API). Actual pricing gates only;
 * depth-refresh policy stays in gate_breakdown.liquidity_refresh for OrderBookTerminal.
 */
import React from 'react';
import { liveFeedLabel } from '../pages/liveFeedUi';

/**
 * ``synthetic_yes_liquidity`` / ``ob_yes_depth``: older writers or stale DB JSON from when innovation/posterior
 * could still be waived purely because YES OB liquidity existed; that path was removed server-side.
 */
const WAIVED_SOFT_LABELS = {
  manual_leave_open: 'Manual leave-open / shaping',
  ob_refresh_override: 'Depth refresh · Override Tier 2 (checkbox)',
  synthetic_yes_liquidity: 'Stale snapshot — obsolete liquidity waiver',
  ob_yes_depth: 'Stale snapshot — obsolete liquidity waiver',
};

function formatWaivedSoft(token) {
  if (token == null || token === '') return 'group';
  return WAIVED_SOFT_LABELS[token] || String(token).replace(/_/g, ' ');
}

function tierRow(label, active, detail) {
  return (
    <div className="gate-bd-row" key={label}>
      <span className="gate-bd-label">{label}</span>
      <span className={active ? 'gate-bd-on' : 'gate-bd-off'}>{active ? 'blocking' : '—'}</span>
      {detail ? <span className="gate-bd-detail">{detail}</span> : null}
    </div>
  );
}

/** Tier 2: sensor trips show blocking unless the soft group is waived (then overridden). */
function tierSoftSensorRow(label, sensor, waivedAsGroup, waivedSoftToken) {
  let statusText = '—';
  let statusClass = 'gate-bd-off';
  if (sensor && waivedAsGroup) {
    statusText = 'overridden';
    statusClass = 'gate-bd-overridden';
  } else if (sensor) {
    statusText = 'blocking';
    statusClass = 'gate-bd-on';
  }
  const detail = sensor && waivedAsGroup ? `via ${formatWaivedSoft(waivedSoftToken)}` : null;
  return (
    <div className="gate-bd-row" key={label}>
      <span className="gate-bd-label">{label}</span>
      <span className={statusClass}>{statusText}</span>
      {detail ? <span className="gate-bd-detail">{detail}</span> : null}
    </div>
  );
}

function manualFlagRow(label, on) {
  return (
    <div className="gate-bd-row" key={label}>
      <span className="gate-bd-label">{label}</span>
      <span className={on ? 'gate-bd-on' : 'gate-bd-off'}>{on ? 'on' : '—'}</span>
    </div>
  );
}

/** Tier-2 informational: quote allowed but log-CI / books / p-band ⇒ one-sided margination (not a block). */
function tier2PosteriorOneSidedRow(active) {
  return (
    <div className="gate-bd-row" key="posterior-ci-one-sided">
      <span className="gate-bd-label" title="CI / books / p-band → one-sided margination (not a block)">
        Post. one-sided
      </span>
      <span className={active ? 'gate-bd-on' : 'gate-bd-off'}>{active ? 'active' : '—'}</span>
    </div>
  );
}

/** TOB under through max competitor → drop under, stay open (not the over-exhaust close). */
function tier2TobForceOneSidedRow(active, reason) {
  return (
    <div className="gate-bd-row" key="tob-force-one-sided">
      <span
        className="gate-bd-label"
        title="TOB risk / scenario dropped the under — quote stays open over-only"
      >
        TOB · one-sided
      </span>
      <span className={active ? 'gate-bd-on' : 'gate-bd-off'}>{active ? 'active' : '—'}</span>
      {active && reason ? <span className="gate-bd-detail">{reason}</span> : null}
    </div>
  );
}

export default function GateBreakdownPanel({ quote }) {
  const bd = quote?.gate_breakdown;
  /** Entries path: DB ``quote_source`` + breakdown flag (older rows lacked persisted ``quote_source``). */
  const entriesQuotePath =
    quote?.quote_source === 'entries' || Boolean(bd?.quote_uses_entries_gate_merge);
  if (!bd) {
    return (
      <div className="card chart-card gate-breakdown-card">
        <h3>Gate breakdown</h3>
        <p className="gate-breakdown-empty">No breakdown on this quote (refresh or select a live market).</p>
      </div>
    );
  }

  const t1 = bd.tier1_hard || {};
  const primaryFeedLabel = liveFeedLabel(t1.live_feed?.source);
  const soft = bd.soft_engine || {};
  const ob = bd.ob_liquidity || {};
  const op = bd.operational || {};
  const lu = bd.lineups || {};
  const mi = bd.manual_intervention_flags || {};
  const dom = bd.dominant_blocker;

  const inn = soft.innovation || {};
  const post = soft.posterior || {};
  const tob = soft.tob_margin || {};
  const waivedSoft = soft.waived_by;

  const rotowireHold = Boolean(lu.rotowire_hold ?? op.rotowire);
  const manualLineupHold = Boolean(lu.manual_lineup_hold ?? op.manual_lineup);
  const manualSuspend = Boolean(mi.suspend ?? op.manual_suspend);
  const manualLeaveOpenShaping = Boolean(mi.leave_open_shaping);
  const manualTwoSidedSoft = Boolean(mi.two_sided_waives_soft_engine);
  const manualOneSidedSoft = Boolean(mi.one_sided_waives_soft_engine);
  const activeCodes = Array.isArray(bd.active_block_codes) ? bd.active_block_codes : [];

  return (
    <div className="card chart-card gate-breakdown-card">
      <h3>Gate breakdown</h3>
      <div className={`gate-bd-quote ${bd.quote_allowed ? 'gate-bd-allowed' : 'gate-bd-blocked'}`}>
        Quote: <strong>{bd.quote_allowed ? 'Allowed' : 'Blocked'}</strong>
        {dom ? (
          <span className="gate-bd-dom" title="Primary closure path when blocked">
            {' '}
            · {dom.replace(/_/g, ' ')}
          </span>
        ) : null}
      </div>

      {bd.pricing_model ? (
        <div className="gate-bd-section">
          <div className="gate-bd-section-title">Pricing model</div>
          <div
            className="gate-bd-row"
            title="Distribution actually used for this market's alt ladder and line remap. FALLBACK means the configured primary law was unavailable (e.g. compound_gamma with no paired count market); REFUSED means no ladder or remap was produced."
          >
            <span
              className={
                /FALLBACK|REFUSED/.test(bd.pricing_model) ? 'gate-bd-on' : 'gate-bd-off'
              }
            >
              {/FALLBACK/.test(bd.pricing_model)
                ? 'fallback'
                : /REFUSED/.test(bd.pricing_model)
                ? 'refused'
                : 'primary'}
            </span>
            <span className="gate-bd-detail">{bd.pricing_model}</span>
          </div>
        </div>
      ) : null}

      {!bd.quote_allowed && activeCodes.length > 0 ? (
        <div className="gate-bd-section">
          <div className="gate-bd-section-title">Block codes</div>
          <div className="gate-bd-row gate-bd-codes" title="All sensors contributing while blocked (not display priority only)">
            <span className="gate-bd-detail">{activeCodes.join(', ')}</span>
          </div>
        </div>
      ) : null}

      <div className="gate-bd-section">
        <div className="gate-bd-section-title">Tier 1 · hard</div>
        {tierRow('Pre-game', Boolean(t1.pre_game?.active))}
        {tierRow('Min books', Boolean(t1.min_books?.active))}
        {tierRow(
          'Derived parents',
          Boolean(t1.derived_parents?.active),
          t1.derived_parents?.reason || null
        )}
        {tierRow(
          `${primaryFeedLabel} live`,
          Boolean(t1.opta_live?.active),
          t1.opta_live?.countdown_s != null && t1.opta_live?.active
            ? `${t1.opta_live.countdown_s}s`
            : t1.opta_live?.reason || null
        )}
        {tierRow(
          'Kalshi live',
          Boolean(t1.kalshi_live?.active),
          t1.kalshi_live?.reason || null
        )}
        {tierRow(
          'Live feed',
          Boolean(t1.live_feed?.active),
          t1.live_feed?.reason || null
        )}
        {tierRow('Fixture suspended', Boolean(t1.fixture_suspended?.active))}
        {t1.blocks_synthetic_liquidity ? (
          <div className="gate-bd-note">Synthetic liquidity ops blocked while tier 1 is active.</div>
        ) : null}
      </div>

      <div className="gate-bd-section">
        <div className="gate-bd-section-title">Tier 2 · soft sensors</div>
        {tierSoftSensorRow('Innovation', Boolean(inn.sensor), Boolean(inn.waived_as_group), waivedSoft)}
        {tierSoftSensorRow(
          entriesQuotePath ? 'Posterior (Entries)' : 'Posterior',
          Boolean(post.sensor),
          Boolean(post.waived_as_group),
          waivedSoft,
        )}
        {tierSoftSensorRow(
          tob.tob_over != null || tob.tob_under != null
            ? `TOB margin${tob.active_cap ? ' · capping' : ''}`
            : 'TOB margin',
          Boolean(tob.sensor),
          Boolean(tob.waived_as_group),
          waivedSoft,
        )}
        {tier2TobForceOneSidedRow(Boolean(tob.force_one_sided), tob.force_one_reason || null)}
        {tier2PosteriorOneSidedRow(Boolean(soft.posterior_ci_one_sided?.active))}
      </div>

      <div className="gate-bd-section">
        <div className="gate-bd-section-title">Tier 3 · liquidity</div>
        {tierRow('No YES depth', Boolean(ob.no_yes_depth))}
        {tierRow('Over-only OB', Boolean(ob.one_sided_over_only))}
      </div>

      <div className="gate-bd-section">
        <div className="gate-bd-section-title">Lineups</div>
        {tierRow('Rotowire', rotowireHold)}
        {tierRow('Manual lineup', manualLineupHold)}
      </div>

      <div className="gate-bd-section gate-bd-section-last">
        <div className="gate-bd-section-title">Overrides</div>
        {manualFlagRow('Manual suspend', manualSuspend)}
        {manualFlagRow('Leave-open shaping', manualLeaveOpenShaping)}
        {manualFlagRow('Two-sided waiver', manualTwoSidedSoft)}
        {manualFlagRow('One-sided waiver', manualOneSidedSoft)}
        {manualFlagRow('Force one-sided', Boolean(mi.force_one_sided))}
        {manualFlagRow('Force two-sided', Boolean(mi.force_two_sided))}
      </div>
    </div>
  );
}
